import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { Toaster } from 'react-hot-toast'
import App from './App'
import { queryClient } from './lib/queryClient'
import { registerSW, unregisterSW, setupOnlineSync } from './lib/syncManager'
import { detectHost, isCustomHostCandidate } from './lib/host/detectHost'
import './lib/sentry'
import '@fontsource/maven-pro/400.css'
import '@fontsource/maven-pro/500.css'
import '@fontsource/maven-pro/700.css'
import './index.css'
import './lib/leaflet-setup'

// Register service worker and online sync handler. Skip SW registration on
// storefront hosts — each subdomain is a separate origin, and a per-origin SW
// would precache/serve the platform app shell on customer storefronts. This
// also covers custom-domain shops (e.g. naturesnurturefood.com): detectHost
// classifies those as "platform" (they are resolved server-side via
// isCustomHostCandidate, exactly as App.tsx mounts the storefront), so without
// this second gate the SW would install and serve its offline app-shell
// fallback on the shop, breaking the storefront.
const _baseDomain = import.meta.env.VITE_STOREFRONT_BASE_DOMAIN || 'palstro.com'
const _host = detectHost(
  window.location.hostname,
  window.location.pathname,
  _baseDomain,
)
if (_host.mode !== 'storefront' && !isCustomHostCandidate(window.location.hostname, _baseDomain)) {
  registerSW()
} else {
  // Storefront origin (subdomain or custom domain). Heal any SW an older build
  // wrongly installed here so shoppers stop getting the offline app-shell.
  unregisterSW()
}
setupOnlineSync()

const SentryApp = Sentry.withProfiler(App)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SentryApp />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3500,
            style: {
              borderRadius: '12px',
              background: '#1f2937',
              color: '#f9fafb',
              fontSize: '14px',
              fontWeight: '500',
            },
            success: {
              iconTheme: { primary: '#22c55e', secondary: '#fff' },
            },
            error: {
              iconTheme: { primary: '#ef4444', secondary: '#fff' },
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
